import {getCountries, ParseError, parsePhoneNumber} from "libphonenumber-js/mobile";

/**
 * Creates a new element, and copies attributes from the original element.
 * @param {HTMLElement} $element
 * @returns {HTMLElement}
 */
function replicateElement($element) {
	const $newElement = document.createElement($element.tagName);
	$element.getAttributeNames().forEach(attributeName => {
		$newElement.setAttribute(attributeName, $element.getAttribute(attributeName));
	});
	return $newElement;
}

/**
 * Gets a flag emoji for a country code.
 * @param {string} code Must be uppercase
 * @returns {string}
 */
function getFlagEmojiForCountryCode(code) {
	if (!/^[A-Z]{2}$/.test(code)) {
		throw new Error(`Invalid country code: ${code}`);
	}
	const codes = code
		.split('')
		.map(char => 127397 + char.charCodeAt());
	return String.fromCodePoint(...codes);
}

/**
 * Creates a country dropdown select field
 * @returns {HTMLSelectElement}
 */
function createCountryDropdownElement() {
	const $dropdown = document.createElement('select');

	//create entries for all country codes
	getCountries().forEach(countryCode => {
		const $option = document.createElement('option');
		$option.value = countryCode;
		$option.text = countryCode + ' - ' + getFlagEmojiForCountryCode(countryCode);
		$dropdown.appendChild($option);
	});

	return $dropdown;
}

/**
 * Given a telephone input, attempt to extend the element to support country code selection.
 * @param $input
 */
export default function makeMobileInput($input) {
	if ($input.tagName !== 'INPUT') {
		console.error(`makeMobileInput() can only be used on <input> elements. Field is ${$input.tagName}`);
		return;
	}

	//ensure it's an appropriate type
	if (['tel', 'text'].indexOf($input.getAttribute('type')) === -1) {
		console.error('Input is not an appropriate type! Should be `tel` or `text`...');
		return;
	}

	/**
	 * If the input is fresh, we might update the country based on other values, or do some other things
	 * @type {boolean}
	 */
	let inputIsFresh = true;

	const invalidInputMessage = $input.dataset.woocommerceMessagemediaMobileInvalidMessage || 'Invalid mobile phone number';
	const defaultCountryCode = $input.dataset.woocommerceMessagemediaMobileDefaultCountryCode || 'AU';

	//determine or generate a name
	const inputName = '__user_input_' + (
		$input.hasAttribute('name')
			? $input.getAttribute('name')
			: `woocommerce_messagemedia_${Math.random().toString(36).substring(7)}`
	);

	//determine an ID, otherwise use the input name
	const inputId = '__user_input_' + (
		$input.hasAttribute('id')
			? $input.getAttribute('id')
			: inputName
	);

	//create/update the value of the data attribute
	$input.dataset.woocommerceMessagemediaMobileInput = 'initialized';

	//create a wrapper for new elements
	const $wrapper = document.createElement('div');
	$wrapper.classList.add('woocommerce-messagemedia-mobile-input-wrapper');

	const $controls = document.createElement('div');
	$controls.classList.add('woocommerce-messagemedia-mobile-input-controls');

	//create a new input and set name and id
	const $userInput = replicateElement($input);
	$userInput.setAttribute('name', inputName);
	$userInput.setAttribute('id', inputId);

	//remove the required attribute on the original input if set (this can result
	//in errors depending on user input provided, eg invalid mobile number means
	//value present in user input, but not original input)
	$input.removeAttribute('required');

	//add a class to hide the main input
	$input.classList.add('woocommerce-messagemedia-mobile-input-hidden');

	//get a country code dropdown, and insert before user input
	const $countryDropdown = createCountryDropdownElement();
	$countryDropdown.setAttribute('name', `${inputName}_country`);

	const $dropdownDecoration = document.createElement('span');
	$dropdownDecoration.setAttribute('aria-hidden', 'true');
	$dropdownDecoration.setAttribute('role', 'presentation');
	$dropdownDecoration.classList.add('woocommerce-messagemedia-mobile-input-dropdown-decoration');

	//append country code and input to wrapper
	$controls.appendChild($countryDropdown);
	$controls.appendChild($dropdownDecoration);
	$controls.appendChild($userInput);

	$wrapper.appendChild($controls);

	//append a summary value element to the wrapper
	const $summary = document.createElement('span');
	$summary.classList.add('woocommerce-messagemedia-mobile-input-summary');
	$wrapper.appendChild($summary);

	//insert wrapper before input
	$input.parentNode.insertBefore($wrapper, $input);

	//set the update value event
	$userInput.addEventListener('input', () => {
		const userValue = $userInput.value.trim();
		let value;
		let message = false;
		$userInput.setCustomValidity('');

		//user has changed the input, not fresh anymore, keep whatever they enter...
		inputIsFresh = false;

		//attempt to parse the entered number...
		try {
			const result = parsePhoneNumber(
				userValue,
				countryCode
			);

			if (userValue.length > 0 && !result.isValid()) {
				value = '';
				message = invalidInputMessage;
				$userInput.setCustomValidity(message);
			} else {
				value = result.format('E.164')
			}
		} catch (error) {
			value = '';
			if (error instanceof ParseError) {
				if ($userInput.value.length > 0) {
					message = invalidInputMessage;
					$userInput.setCustomValidity(message);
				}
				console.error(error.message);
			} else {
				throw error;
			}
		}
		$input.value = value;
		$summary.innerText = message || value;
	});

	let countryCode = defaultCountryCode;

	$countryDropdown.addEventListener('input', () => {
		countryCode = $countryDropdown.value;
		//dispatch the input event to update the main field
		$userInput.dispatchEvent(new Event('input'));
	});

	//if there was a value on the input, parse the value
	if ($input.value) {
		inputIsFresh = false;

		try {
			const parsed = parsePhoneNumber($input.value);
			$userInput.value = parsed.formatNational();
			countryCode = parsed.country;
			//dispatch the input event to update the main field
			$userInput.dispatchEvent(new Event('input'));
		} catch (error) {
			if (error instanceof ParseError) {
				console.error(error.message);
			} else {
				throw error;
			}
		}
	}

	const externalCountryFieldSelector = $input.dataset.woocommerceMessagemediaCountryInput;

	if (inputIsFresh && externalCountryFieldSelector) {
		const countryChangeCallback = event => {
			//bail if it doesn't match the selector
			if (!event.target.matches(externalCountryFieldSelector)) {
				return;
			}

			//input is not fresh, don't make a change
			if (!inputIsFresh) {
				return;
			}

			if (getCountries().indexOf(event.target.value) < 0) {
				console.warn('invalid country code for mobile input', event.target.value);
				return;
			}

			$countryDropdown.value = event.target.value;
		};

		document.addEventListener('input', countryChangeCallback);
		//woocommerce uses lots of jQ (including Select2), so none of the events are native or compat with native in
		//the usual bubbling way.
		if (jQuery) {
			jQuery(document).on('change', externalCountryFieldSelector, countryChangeCallback);
		}

		//try set initial value from external field
		const $countryField = document.querySelector(externalCountryFieldSelector);
		if ($countryField && getCountries().indexOf($countryField.value) >= 0) {
			countryCode = $countryField.value;
		}
	}

	//select the default country code in the dropdown
	$countryDropdown.value = countryCode;

	//dispatch an event so that other code can respond when this is initialised
	$input.dispatchEvent(new CustomEvent('woocommerce-messagemedia-mobile-input-initialized',
		{bubbles: true,}
	));
}
